import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { resetAllAddressAction } from '~/address/store/address.reducer'
import { resetAgentData } from '~/agent/store/agent.reducer'
import { resetBundles } from '~/bundles/store/bundles.reducer'
import { resetCheckout } from '~/checkout/store/checkout.reducer'
import { selectContractId } from '~/checkout/store/checkout.selectors'
import { resetContract } from '~/contract/store/contract.reducer'
import { useAppDepsContext } from '~/core/hooks/useAppDepsContext'
import { resetCustomer } from '~/customer/store/customer.reducer'
import { selectDocType, selectDocId } from '~/customer/store/customer.selectors'
import { resetLegalTexts } from '~/legal/store/legalTexts/legalTexts.reducer'
import { resetNextSteps } from '~/legal/store/nextSteps/nextSteps.reducer'
import { resetLines } from '~/line/store/line.reducer'
import { resetMasterData } from '~/masterData/store/masterData.reducer'
import { CANCEL_SALE, RESTART_SALE, EXIT_SALE } from '~/modal/constants/messages'
import useModal from '~/modal/hooks/useModal'
import ModalMessages from '~/modal/models/modal.model'
import { resetModal } from '~/modal/store/modal.reducer'
import { resetPrices } from '~/prices/store/prices.reducer'
import useSalesQueryParams from '~/router/hooks/useQueryParams'
import PATHS from '~/router/paths'
import { resetForm, resetSaleFlow } from '~/sale/store/sale.reducer'
import { resetTariffs } from '~/tariff/store/tariff.reducer'
import useTheme from '~/ui/theme/useTheme'

interface useResetSaleReturn {
  resetSale: (brand?: string) => void
  selectResetSaleModalMessage: (message: string) => void
}

export default function useResetSale(): useResetSaleReturn {
  const dispatch = useDispatch()
  const history = useHistory()
  const { theme } = useTheme()
  const { brand, documentType, documentValue, contractId } = useSalesQueryParams()
  const { vistaApiGateway } = useAppDepsContext().runtimeConfig
  const { selectAndOpenModalFromList } = useModal()

  const docType = useSelector(selectDocType)
  const docId = useSelector(selectDocId)
  const currentCustomerContractId = useSelector(selectContractId)

  function resetStore() {
    /**
     * We only reset sale flow dependant modules
     *
     * App dependant modules: agent, theme, featureFlag
     *
     * Sale dependant modules: masterData, customer, lines, tariffs, saleFlow,
     * saleForm, address, checkout, bundles, prices, legalTexts, modal,
     * contract & nextSteps
     */
    dispatch(resetAgentData())
    dispatch(resetMasterData())
    dispatch(resetCustomer())
    dispatch(resetLines())
    dispatch(resetTariffs())
    dispatch(resetSaleFlow())
    dispatch(resetAllAddressAction())
    dispatch(resetCheckout())
    dispatch(resetBundles())
    dispatch(resetForm())
    dispatch(resetPrices())
    dispatch(resetLegalTexts())
    dispatch(resetModal())
    dispatch(resetContract())
    dispatch(resetNextSteps())
  }

  function resetSale(brand?: string) {
    resetStore()
    if (history.location.pathname !== PATHS.SALES_START)
      window.location.href = `${window.location.origin}${PATHS.SALES_START}?brand=${brand}`
  }

  function navigateToVista() {
    resetStore()
    if (!contractId || !documentValue) {
      window.location.href = `${vistaApiGateway}?brand=${brand}&documentType=${documentType}`
      return
    }
    const vistaURL = `${vistaApiGateway}/${documentValue}/offers-configurator/${contractId}?brand=${brand}&documentType=${documentType}`
    window.location.href = vistaURL
  }

  function navigateToVistaWithCurrentClient() {
    const vistaURL = `${vistaApiGateway}/${docId}/offers-configurator/${currentCustomerContractId}?brand=${brand}&documentType=${docType}`
    window.location.href = vistaURL
  }

  const resetModalMessages: ModalMessages = {
    CANCEL_SALE: {
      open: false,
      title: CANCEL_SALE.title,
      description: CANCEL_SALE.description,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: CANCEL_SALE.actions.continue,
          fn: () => resetSale(theme.name),
        },
        {
          button: { variant: 'outlined', size: 'medium', color: 'secondary' },
          name: CANCEL_SALE.actions.cancel,
        },
      ],
      type: 'warning',
    },
    RESTART_SALE: {
      open: false,
      title: RESTART_SALE.title,
      description: RESTART_SALE.description,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: RESTART_SALE.actions.continue,
          fn: () => resetSale(theme.name),
        },
        {
          button: { variant: 'outlined', size: 'medium', color: 'secondary' },
          name: RESTART_SALE.actions.cancel,
        },
      ],
      type: 'warning',
    },
    EXIT_SALE: {
      open: false,
      title: EXIT_SALE.title,
      description: EXIT_SALE.description,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: EXIT_SALE.actions.continue,
          fn: navigateToVista,
        },
        {
          button: { variant: 'outlined', size: 'medium', color: 'secondary' },
          name: EXIT_SALE.actions.cancel,
        },
      ],
      type: 'warning',
    },
    EXIT_SALE_CURRENT_CLIENT: {
      open: false,
      title: EXIT_SALE.title,
      description: EXIT_SALE.description,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: EXIT_SALE.actions.continue,
          fn: navigateToVistaWithCurrentClient,
        },
        {
          button: { variant: 'outlined', size: 'medium', color: 'secondary' },
          name: EXIT_SALE.actions.cancel,
        },
      ],
      type: 'warning',
    },
  }

  function selectResetSaleModalMessage(messageType: string) {
    selectAndOpenModalFromList(messageType, resetModalMessages)
  }

  return {
    resetSale,
    selectResetSaleModalMessage,
  }
}
